import React, { useState } from 'react';
import Radio from 'src/components/Radio/Radio';

import AiCommonModal from './AiCommonModal';

interface IProps {
    title: string;
    savedAddresses: string[];
    onSubmit: (address: string) => void;
    onClose: () => void;
}

// Mirrors AiQuestionSelection's row design (number badge, title, checkbox, divide-y list) but as
// a single unified list that also includes a "Please specify" row swapping to a live text input —
// AiQuestionSelection itself has no slot for that, so this replicates its classNames directly.
const AddressSelectionCard = ({ title, savedAddresses, onSubmit, onClose }: IProps) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [customAddress, setCustomAddress] = useState('');
    const customIndex = savedAddresses.length;
    const isCustomSelected = selectedIndex === customIndex;
    const canSubmit = !isCustomSelected || customAddress.trim().length > 0;

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit(isCustomSelected ? customAddress.trim() : savedAddresses[selectedIndex]);
    };

    return (
        <AiCommonModal title={title} agent="kate" handleClose={onClose} primaryBtnText="Submit" primaryBtnOnClick={handleSubmit} primaryBtnDisabled={!canSubmit}>
            <ul className="rounded-lg border border-utilityGray200 shadow-xs divide-y divide-utilityGray200 overflow-hidden">
                {savedAddresses.map((address, index) => {
                    const isSelected = selectedIndex === index;

                    return (
                        <li
                            key={address}
                            onClick={() => setSelectedIndex(index)}
                            className={`flex items-center gap-2 p-3 cursor-pointer transition-colors duration-150 hover:bg-utilityGray100 group ${isSelected ? 'bg-utilityGray100' : 'bg-white'}`}
                        >
                            <div className="flex-shrink-0">
                                <span className={`table-profile-customer-circle-xs !text-[10px] group-hover:bg-primary group-hover:text-white ${isSelected ? '!bg-primary !text-white' : ''}`}>
                                    {index + 1}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-textSecondary text-xs leading-[1.5] font-medium">{address}</p>
                            </div>
                            <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Radio id={`address-option-${index}`} inputName="address-option" checked={isSelected} onChangeFunc={() => setSelectedIndex(index)} />
                            </div>
                        </li>
                    );
                })}

                <li
                    onClick={() => setSelectedIndex(customIndex)}
                    className={`flex items-center gap-2 p-3 cursor-pointer transition-colors duration-150 hover:bg-utilityGray100 group ${isCustomSelected ? 'bg-utilityGray100' : 'bg-white'}`}
                >
                    <div className="flex-shrink-0">
                        <span className={`table-profile-customer-circle-xs !text-[10px] group-hover:bg-primary group-hover:text-white ${isCustomSelected ? '!bg-primary !text-white' : ''}`}>
                            {customIndex + 1}
                        </span>
                    </div>
                    <div className="flex-1 min-w-0">
                        {isCustomSelected ? (
                            <input
                                type="text"
                                autoFocus
                                value={customAddress}
                                onChange={(e) => setCustomAddress(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Enter an address"
                                className="w-full text-textSecondary text-xs leading-[1.5] font-medium bg-transparent outline-none border-0 p-0"
                            />
                        ) : (
                            <p className="text-textSecondary text-xs leading-[1.5] font-medium">Please specify</p>
                        )}
                    </div>
                    <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Radio id="address-option-custom" inputName="address-option" checked={isCustomSelected} onChangeFunc={() => setSelectedIndex(customIndex)} />
                    </div>
                </li>
            </ul>
        </AiCommonModal>
    );
};

export default AddressSelectionCard;
